import { Params } from '@angular/router';
import { JSONValue } from '@models/common.models';
import { Observable } from 'rxjs';

export interface Option {
  disable: boolean;
  value: number;
  name: string;
  id: string | number;
}
export type RowDataDefinition = Record<
  string,
  string | string[] | CustomRowDefinition | number | Date | boolean | JSONValue
>;
export type CustomRowDefinition = Record<string, string>;

export interface GridData {
  rows: RowDataDefinition[];
  totalPages: number;
  rowCount: number;
}

export interface DataResponse<T> {
  pageSize: number;
  currentPage: number;
  totalCount: number;
  items: T[];
}

export interface RequestIdFilterItem {
  entityId: string;
  requestId: string;
}

export interface ItemIdFilterItem {
  entityId: string;
  itemId: string;
}

export interface FilterGroup {
  logicalGroupOperator: number;
  entityPropertyFilters: PropertyFilter[];
}

export interface PropertyFilter {
  propertyName: string;
  field?: string;
  conditionPropertyOperator: number;
  value: string | JSONValue;
}

export interface ConnectorStatus {
  isLoading: boolean;
}

export type sortDirectionType = 'asc' | 'desc';

export interface IDataConnector {
  getRows$(): Observable<GridData>;
  getStatus$(): Observable<ConnectorStatus>;
  getDataParams$(): Observable<Params>;
  onSearch$(searchTerm: string): Observable<GridData>;
  onPageChange(page: number, pageSize: number): void;
  onSort(columnName: string | string[] | null, sortDirection: sortDirectionType | null): void;
  onFilter(filter: FilterGroup[]): void;
  onSearch(searchTerm: string): void;
  setFilters(filter: FilterGroup[]): void;
  setPageNumber(page: number): void;
  setStatus(status: ConnectorStatus): void;
  refresh(): void;
  refresh$(): Observable<GridData>;

  sortBy: string | null;
  sortDirection: sortDirectionType | null;
  pageSize: number | null;
}

export enum RequestStatus {
  Initiating = 995430001,
  Submitted = 465190000,
  InProgress = 465190001,
  PendingInformation = 465190002,
  Completed = 465190003,
  Cancelled = 310700001,
}

export enum RequestStatusOrder {
  Initiating = 1,
  Submitted = 2,
  InProgress = 3,
  PendingInformation = 4,
  Completed = 5,
  Cancelled = 6,
}

export enum RequestType {
  Classification = 310700000,
  Reclassification = 310700002,
}

export enum RequestTypeOrder {
  Classification = 1,
  Reclassification = 2,
}

export enum RequestReason {
  GovernmentalOrHS_Changes = 310700000,
  AdditionalInformationReceived = 310700001,
  DisputeBrokerRequest = 310700002,
  DisputeCustomsDecision = 310700003,
  DisputeBusinessDecision = 310700004,
  CustomsAction = 310700005,
  BusinessProvidedClassificationReviewedByEY = 310700006,
  EYerror = 310700007,
  FullCodeUpdate = 310700008,
  AdcvdAndOrPgaEnrichment = 310700009,
}
export function getRequestReasonName(requestReason: RequestReason | null): string {
  switch (requestReason) {
    case RequestReason.GovernmentalOrHS_Changes:
      return 'request-reason.governmental-or-HSchanges';
    case RequestReason.AdditionalInformationReceived:
      return 'request-reason.additional-information-received';
    case RequestReason.DisputeBrokerRequest:
      return 'request-reason.dispute-broker-request';
    case RequestReason.DisputeCustomsDecision:
      return 'request-reason.dispute-customs-decision';
    case RequestReason.DisputeBusinessDecision:
      return 'request-reason.dispute-business-decision';
    case RequestReason.CustomsAction:
      return 'request-reason.customs-actions';
    case RequestReason.BusinessProvidedClassificationReviewedByEY:
      return 'request-reason.business-provided-classification-reviewd-by-ey';
    case RequestReason.EYerror:
      return 'request-reason.ey-error';
    case RequestReason.FullCodeUpdate:
      return 'request-reason.full-code-update';
    case RequestReason.AdcvdAndOrPgaEnrichment:
      return 'request-reason.adcvd-and-or-pga-enrichment';
    default:
      return 'request-reason.unknown';
  }
}
export function getItemCancellationReasonName(cancellationReason: CancellationReason | null): string {
  switch (cancellationReason) {
    case CancellationReason.ClassifiedByRequestor:
      return 'item-cancellation-reason.classified-by-requestor';
    case CancellationReason.DiscontinuedProduct:
      return 'item-cancellation-reason.discontinued-product';
    case CancellationReason.EYError:
      return 'item-cancellation-reason.ey-error';
    case CancellationReason.IntangibleGood:
      return 'item-cancellation-reason.intangible-good';
    case CancellationReason.RequestorError:
      return 'item-cancellation-reason.requestor-error';
    case CancellationReason.Allitemscancelled:
      return 'item-cancellation-reason.all-items-cancelled';
    default:
      return 'item-cancellation-reason.unknown';
  }
}
export function getRequestTypeName(type: RequestType | null): string {
  if (type === undefined || type === null) return '';

  switch (type) {
    case RequestType.Classification:
      return 'requests.type.classification';
    case RequestType.Reclassification:
      return 'requests.type.reclassification';
    default:
      return 'requests.type.unknown';
  }
}
export enum CancellationReason {
  ClassifiedByRequestor = 310700000,
  DiscontinuedProduct = 310700001,
  EYError = 310700002,
  IntangibleGood = 310700003,
  RequestorError = 310700004,
  Allitemscancelled = 310700005,
}

export interface ClassificationRequest {
  requestEntityId: string;
  requestId: string;
  type: number;
  requestStatus: number;
  requestReason: number;
  cancellationReason: number;
  classifiedProducts: string;
  classifiedItems: string;
  requester: string;
  requesterEntityId: string;
  requesterEmailAddress: string;
  createdOn: string | Date;
  completedDate: string | Date;
  cancellationDate: string | Date;
  requestComment: string;
  itemsStatusCounters: ItemsStatusCounter[];
  itemsOpenCount: number;
  itemsTotalCount: number;
  itemsDuplicatedCount: number;
  itemsExportJsonCount: number;
  itemsExportJsonSubdivisionsCount: number;
  requestCategory: string;
  isDocArchived: boolean | null;
}

export interface ItemsStatusCounter {
  itemStatus: number;
  count: number;
}

export interface Requester {
  id: string;
  fullname: string;
  emailAddress: string;
}

export interface Country {
  id: string;
  name: string;
}

export interface Region {
  id: string;
  name: string;
}

export interface Subdivision {
  id: string;
  name: string;
}

export interface EndUse {
  id: string;
  name: string;
}

export enum ChipVariant {
  Success = 'success',
  Error = 'error',
  Warning = 'warning',
  MonochromeLight = 'monochrome-light',
  MonochromeDark = 'monochrome-dark',
}

export enum ExportJsonWarningType {
  Compressed = 'compressed',
  MultipleSubdivisions = 'multiple-subdivisions',
}

export enum ConditionPropertyOperator {
  Equal,
  NotEqual,
  GreaterThan,
  LessThan,
  GreaterEqual,
  LessEqual,
  Like,
  NotLike,
  In,
  NotIn,
  On,
  OnOrBefore,
  OnOrAfter,
  ContainsValues,
  Contains,
  DoesNotContain,
  BeginsWith,
  DoesNotBeginWith,
  EndsWith,
  DoesNotEndWith,
}

export const PageSizeOptions: Option[] = [
  { id: 1, value: 10, name: '10', disable: false },
  { id: 2, value: 25, name: '25', disable: false },
  { id: 3, value: 50, name: '50', disable: false },
  { id: 4, value: 100, name: '100', disable: false },
] as const;

export const MAX_COLUMN_WIDTH = 500;
export const MIN_COLUMN_WIDTH = 120;
